import { CalendarDate, parseDate } from '@internationalized/date'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/sl'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

export default dayjs

/** The wire format for a calendar date (in the tenant's timezone). */
export const DATE_FORMAT = 'YYYY-MM-DD'

/**
 * The timezone every time on screen is shown in: the tenant's, not the browser's. A desk
 * in another country still has to see the times its customers were given.
 */
let zone = 'Europe/Ljubljana'

export const setZone = (tz: string | null | undefined) => {
    if (tz) zone = tz
}

export const getZone = (): string => zone

/** An API instant, in the tenant's timezone. */
export const inZone = (value: string) => dayjs(value).tz(zone)

export const formatTime = (value?: string | null): string =>
    value ? inZone(value).format('HH:mm') : ''

export const formatDate = (value?: string | null): string =>
    value ? inZone(value).format('DD.MM.YYYY') : ''

export const formatDateTime = (value?: string | null): string =>
    value ? inZone(value).format('DD.MM.YYYY HH:mm') : ''

/** A YYYY-MM-DD calendar date shown to people. */
export const formatDay = (value?: string | null): string =>
    value ? dayjs(value, DATE_FORMAT).format('ddd DD.MM.YYYY') : ''

/** Today in the tenant's timezone, YYYY-MM-DD. */
export const todayInZone = (): string => dayjs().tz(zone).format(DATE_FORMAT)

/** N days from a YYYY-MM-DD date, still YYYY-MM-DD. */
export const shiftDay = (value: string, days: number): string =>
    dayjs(value, DATE_FORMAT).add(days, 'day').format(DATE_FORMAT)

/** The YYYY-MM-DD an instant falls on in the tenant's timezone. */
export const dayOf = (value: string): string => inZone(value).format(DATE_FORMAT)

/** A `datetime-local` input value (tenant wall clock) → ISO instant. */
export const localInputToIso = (value: string): string =>
    value ? dayjs.tz(value, 'YYYY-MM-DDTHH:mm', zone).toISOString() : ''

/** An ISO instant → a `datetime-local` input value in the tenant's wall clock. */
export const isoToLocalInput = (value?: string | null): string =>
    value ? inZone(value).format('YYYY-MM-DDTHH:mm') : ''

export const toCalendarDate = (value?: string | null): CalendarDate | undefined =>
    value ? parseDate(value) : undefined

export const fromCalendarDate = (value: CalendarDate | null | undefined): string =>
    value ? value.toString() : ''

/** Minutes from local midnight → "HH:MM". */
export const minutesToClock = (minutes: number): string => {
    const h = Math.floor(minutes / 60)
    const m = minutes % 60

    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** "HH:MM" → minutes from local midnight; NaN when it is not a time. */
export const clockToMinutes = (value: string): number => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
    if (!match) return NaN

    const h = Number(match[1])
    const m = Number(match[2])
    if (h > 24 || m > 59 || (h === 24 && m > 0)) return NaN

    return h * 60 + m
}

/**
 * The span an entry code opens a gate in, said once: "26. 9. 2026 od 08:00 do 08:30"
 * when it is one day, both dates only when it runs past midnight.
 */
export const accessWindow = (from: string, until: string, locale: string): { sameDay: boolean, day: string, from: string, until: string } => {
    const start = inZone(from)
    const end   = inZone(until)
    const date  = (value: ReturnType<typeof inZone>) => value.format(locale === 'sl' ? 'D. M. YYYY' : 'D/M/YYYY')
    const sameDay = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')

    return sameDay
        ? { sameDay, day: date(start), from: start.format('HH:mm'), until: end.format('HH:mm') }
        : { sameDay, day: '', from: `${date(start)} ${start.format('HH:mm')}`, until: `${date(end)} ${end.format('HH:mm')}` }
}

/**
 * The starts of a recurring series, as the API makes them: the first one, then every
 * interval days (or weeks) at the same wall-clock time in the tenant's zone, up to count
 * or until (inclusive), never more than 52.
 */
export const seriesOccurrences = (
    first: string,
    recurrence: { frequency: 'daily' | 'weekly', interval: number, count: number, until: string },
): dayjs.Dayjs[] => {
    if (!first) return []

    const local = inZone(first)
    const time  = local.format('HH:mm')
    const step  = Math.max(1, recurrence.interval) * (recurrence.frequency === 'weekly' ? 7 : 1)
    const until = recurrence.until ? dayjs.tz(recurrence.until, DATE_FORMAT, zone).add(1, 'day') : null

    const starts: dayjs.Dayjs[] = []

    for (let i = 0; i < 52; i++) {
        if (recurrence.count > 0 && i >= recurrence.count) break

        // Calendar arithmetic on the local date, so a clock change never shifts the hour.
        const day   = dayjs(local.format(DATE_FORMAT), DATE_FORMAT).add(i * step, 'day').format(DATE_FORMAT)
        const start = dayjs.tz(`${day} ${time}`, 'YYYY-MM-DD HH:mm', zone)

        if (until && !start.isBefore(until)) break

        starts.push(start)
    }

    return starts
}
